import * as React from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { PayrollPoliciesDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import { trackPayrollResultsSubmitted } from 'modules/bookkeepers/lib/amplitudeData'
import payrollResults from 'modules/bookkeepers/store/collections/payrollResults'

import type { OutletProps } from 'modules/bookkeepers/components/PayrollCalculations'
import SummaryComponent from './_SummaryComponent'
import UploadFileComponent from './_UploadFile'

const STEP_IDENTIFIERS = {
  upload_payslip: 'upload_payslip',
  upload_payroll_summary: 'upload_payroll_summary',
  close_calculation: 'close_calculation',
}

const ClosePayrollCalculation = () => {
  const TOTAL_STEPS = Object.keys(STEP_IDENTIFIERS).length
  const [currentStep, setCurrentStep] = React.useState<number>(1)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { policyId } = useParams()
  const policyIdNumber = String(policyId)
  const bookkeeperContext = {
    isBookkeeperView: true,
  }
  const [payslipDocuments, setPayslipDocuments] = React.useState<File[]>([])
  const [payrollSummaryDocuments, setPayrollSummaryDocuments] = React.useState<File[]>([])
  const { currentPolicyPeriod, onUploadCompleted }: OutletProps = useOutletContext()

  const { data: policiesData } = useQuery(PayrollPoliciesDocument, {
    context: bookkeeperContext,
    variables: {
      policyIds: [policyIdNumber],
    },
  })

  const policies = policiesData?.payroll.policiesConnection.nodes || []
  const currentPolicy = policies.find((policy) => policy.id === policyIdNumber)
  const policyCompanyId = currentPolicy?.company.id
  const companyName = companies.get(policyCompanyId)?.get('name')

  const nextStep = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1)
    } else {
      onUploadFiles()
    }
  }

  const onClose = () => {
    if (!currentPolicyPeriod) return

    return navigate(
      navigation.bookkeepers.clients.payrollResults.payrollPolicy.payrollPolicyPeriodId.fullPath({
        periodId: currentPolicyPeriod.attributes.id,
        policyId: policyIdNumber,
      })
    )
  }

  const prevStep = () => {
    if (currentStep <= 1) {
      onClose()
    } else {
      setCurrentStep(currentStep - 1)
    }
  }
  const onFileAdded = (files: File[], step: string) => {
    if (step === STEP_IDENTIFIERS.upload_payslip) {
      setPayslipDocuments(payslipDocuments.concat(files))
    } else if (step === STEP_IDENTIFIERS.upload_payroll_summary) {
      setPayrollSummaryDocuments(payrollSummaryDocuments.concat(files))
    }
  }

  const onFileRemoved = (indexToRemove: number, step: string) => {
    if (step === STEP_IDENTIFIERS.upload_payslip) {
      setPayslipDocuments(payslipDocuments.filter((_file, index) => index !== indexToRemove))
    } else if (step === STEP_IDENTIFIERS.upload_payroll_summary) {
      setPayrollSummaryDocuments(
        payrollSummaryDocuments.filter((_file, index) => index !== indexToRemove)
      )
    }
  }

  const getStepTitle = () => {
    if (currentStep === 1) {
      return i18n.t('bookkeepers.clients.export_files.modal_steps.title.payslip_document')
    } else if (currentStep === 2) {
      return i18n.t('bookkeepers.clients.export_files.modal_steps.title.payroll_summary')
    } else {
      return i18n.t('bookkeepers.clients.export_files.modal_steps.title.send_document', {
        selectedCompany: companyName,
      })
    }
  }

  const getStepDescription = () => {
    if (currentStep === 1) {
      return i18n.t(
        'bookkeepers.clients.export_files.modal_steps.description.payslip_document_description'
      )
    } else if (currentStep === 2) {
      return i18n.t(
        'bookkeepers.clients.export_files.modal_steps.description.payroll_summary_description'
      )
    } else {
      return i18n.t(
        'bookkeepers.clients.export_files.modal_steps.description.uploaded_documents_summary',
        {
          selectedCompany: companyName,
        }
      )
    }
  }
  const { running: uploadingFiles, performAction: onUploadFiles } = useAsyncAction(async () => {
    if (!currentPolicyPeriod) {
      return Promise.reject(new Error('currentPolicyPeriod no set up'))
    }

    await payrollResults.changePeriodWithDocuments({
      payslip_documents: payslipDocuments,
      payroll_summary_documents: payrollSummaryDocuments,
      policy_period_id: currentPolicyPeriod.get('id'),
      bookkeeper_view: true,
    })

    trackPayrollResultsSubmitted(
      currentPolicyPeriod.get('name'),
      currentPolicyPeriod.get('id'),
      true,
      true
    )

    onUploadCompleted()

    onClose()
  })

  const firstStepComponent = (
    <UploadFileComponent
      onFileAdded={onFileAdded}
      onFileRemoved={onFileRemoved}
      documents={payslipDocuments}
      label={i18n.t('bookkeepers.clients.export_files.modal_steps.title.payslip_document')}
      stepIdentifier={STEP_IDENTIFIERS.upload_payslip}
    />
  )

  const secondStepComponent = (
    <UploadFileComponent
      onFileAdded={onFileAdded}
      onFileRemoved={onFileRemoved}
      documents={payrollSummaryDocuments}
      label={i18n.t('bookkeepers.clients.export_files.modal_steps.title.payroll_summary')}
      stepIdentifier={STEP_IDENTIFIERS.upload_payroll_summary}
    />
  )

  const summaryStepComponent = (
    <SummaryComponent
      payrollResultFiles={payslipDocuments}
      payrollSummaryFiles={payrollSummaryDocuments}
    />
  )

  const getComponent = () => {
    if (currentStep === 1) {
      return firstStepComponent
    } else if (currentStep === 2) {
      return secondStepComponent
    } else if (currentStep === 3) {
      return summaryStepComponent
    }
  }

  const cancelButton = () => {
    if (currentStep > 1) {
      return i18n.t('common.back')
    } else {
      return i18n.t('common.cancel')
    }
  }

  const nextButton = () => {
    if (currentStep < TOTAL_STEPS) {
      return i18n.t('common.next')
    } else {
      return i18n.t('common.submit')
    }
  }

  const hasNoDocumentsUploaded = () => {
    if (currentStep === TOTAL_STEPS) {
      return payslipDocuments.length === 0 && payrollSummaryDocuments.length === 0
    }
  }

  return (
    <Modal
      illustration="file-upload"
      title={getStepTitle()}
      description={getStepDescription()}
      onClose={onClose}
      onSubmit={nextStep}
      footer={
        <Box flexDirection="row" justifyContent="spaceBetween" width="full">
          <Button disabled={uploadingFiles} type="secondary" onClick={prevStep}>
            {cancelButton()}
          </Button>
          <Button disabled={hasNoDocumentsUploaded() || uploadingFiles} submit>
            {nextButton()}
          </Button>
        </Box>
      }
    >
      {getComponent()}
    </Modal>
  )
}

export default ClosePayrollCalculation
